import EntityTable from "@mirage/components/entityTable/EntityTable";
import LinkCell from "@mirage/components/entityTable/components/LinkCell";
import { Box } from "@mui/system";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import React, { memo, useCallback, useMemo } from "react";
import { SearchResultTableProps } from "./PlanningApplicationsTable";
import BuildingRegulationsService from "@publicPortal/services/BuildingRegulationsService";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";
import { TablePaginationBaseProps } from "@mui/material/TablePagination/TablePagination";

const pp = new PublicPortalRoutingService();
const bcService = new BuildingRegulationsService();

const BuildingRegulationsTable: React.FC<SearchResultTableProps> = memo(({ filter, title, customPageRequest }) => {
    const { isMobile } = useDeviceDetect();
    const proposalColumnDimensions = useMemo(() => (isMobile ? { width: 200, flex: undefined } : undefined), [isMobile]);
    const validationDateColumnDimensions = useMemo(() => (isMobile ? { width: 120, flex: undefined } : undefined), [isMobile]);
    const statusColumnDimensions = useMemo(() => (isMobile ? { width: 120, flex: undefined } : undefined), [isMobile]);
    const decisionColumnDimensions = useMemo(() => (isMobile ? { width: 120, flex: undefined } : undefined), [isMobile]);
    const completionDateColumnDimensions = useMemo(() => (isMobile ? { width: 120, flex: undefined } : undefined), [isMobile]);
    const columns = useMemo(() => {
        return [
            {
                field: "name",
                headerName: "Reference",
                renderCell: (params: GridRenderCellParams) => <LinkCell value={params.value} route={pp.BuildingRegulationsRecordRoute(Number(params.id))} />,
                width: 140,
            },
            { field: "finalProposal", headerName: "Final Proposal", flex: 1, ...proposalColumnDimensions },
            { field: "validationDate", headerName: "Validation Date", flex: 0.25, type: "date", ...validationDateColumnDimensions },
            { field: "decision", headerName: "Decision", flex: 0.25, ...decisionColumnDimensions },
            { field: "status", headerName: "Status", flex: 0.25, ...statusColumnDimensions },
            { field: "completedDate", headerName: "Completion Date", flex: 0.25, type: "date", ...completionDateColumnDimensions },
        ];
    }, [completionDateColumnDimensions, decisionColumnDimensions, proposalColumnDimensions, statusColumnDimensions, validationDateColumnDimensions]);
    const pageRequest = useCallback(
        async (pageRequest: IEntityPageRequest) => {
            if (customPageRequest) {
                return await customPageRequest(pageRequest);
            }
            const response = await bcService.pageRequest(pageRequest);
            return response;
        },
        [customPageRequest]
    );
    const overridePaginationProps = useCallback((baseProps: TablePaginationBaseProps) => ({ ...baseProps, showFirstButton: false, showLastButton: false }), []);

    return (
        <Box sx={{ height: "auto" }}>
            <EntityTable
                title={title}
                key="building-regulations-entityTable"
                stateKey="bcRegulations"
                onPageRequest={pageRequest}
                columns={columns}
                filter={filter}
                hideRowsPerPage={isMobile}
                overridePaginationProps={overridePaginationProps}
            />
        </Box>
    );
});

export default BuildingRegulationsTable;
